import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import { 
  useCandidateLoginMutation, 
  useSendOtpEmailMutation,
  useVerifyOtpEmailMutation 
} from '../../store/api/userApiSlice';
import InputField from './InputField';
import PasswordInput from './PasswordInput';
import PhoneInput from './PhoneInput';
import './Auth.css';

function LoginForm({ onSwitchToSignup, onClose, onSwitchToForgotPassword }) {
  const navigate = useNavigate();
  
  // Redux state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  
  // API mutations - Using candidateLogin for user login (not recruiter)
  const [login, { isLoading: isLoginLoading, error: loginError }] = useCandidateLoginMutation();
  const [sendOtp, { isLoading: isSendingOtp, error: sendOtpError }] = useSendOtpEmailMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp, error: verifyOtpError }] = useVerifyOtpEmailMutation();
  
  const [isOTPMode, setIsOTPMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    otp: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');

  // Redirect on successful login
  useEffect(() => {
    if (isAuthenticated) {
      if (onClose) {
        onClose(); // Close modal if opened from modal
      }
      navigate('/'); // Redirect to home
    }
  }, [isAuthenticated, navigate, onClose]);

  // Handle API errors
  useEffect(() => {
    const error = loginError || sendOtpError || verifyOtpError;
    if (error) {
      setGeneralError(error?.data?.message || 'Operation failed. Please try again.');
    }
  }, [loginError, sendOtpError, verifyOtpError]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isOTPMode) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      }
    } else {
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      }
      
      if (otpSent && !formData.otp) {
        newErrors.otp = 'OTP is required';
      } else if (otpSent && formData.otp.length !== 6) {
        newErrors.otp = 'OTP must be 6 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      if (!isOTPMode) {
        // Password login with RTK Query - using user endpoint
        try {
          await login({ 
            email: formData.email, 
            password: formData.password 
          }).unwrap();
          // Success is handled by the useEffect watching isAuthenticated
        } catch (err) {
          console.error('Login failed:', err);
          // Error is handled by useEffect watching apiError
        }
      } else {
        // OTP login flow
        if (!otpSent) {
          // Step 1: Send OTP
          try {
            await sendOtp({ phone: formData.phone }).unwrap();
            setOtpSent(true);
            setGeneralError('');
          } catch (err) {
            console.error('Failed to send OTP:', err);
          }
        } else {
          // Step 2: Verify OTP
          try {
            await verifyOtp({ 
              phone: formData.phone, 
              otp: formData.otp 
            }).unwrap();
            // Success handled by isAuthenticated effect
          } catch (err) {
            console.error('OTP verification failed:', err);
          }
        }
      }
    }
  };

  const isLoading = isLoginLoading || isSendingOtp || isVerifyingOtp;

  const handleOTPLogin = () => {
    setIsOTPMode(true);
    setOtpSent(false);
    setFormData({ ...formData, password: '', otp: '' });
    setErrors({});
    setGeneralError('');
  };

  const handlePasswordLogin = () => {
    setIsOTPMode(false);
    setOtpSent(false);
    setFormData({ ...formData, phone: '', otp: '' });
    setErrors({});
    setGeneralError('');
  };

  return (
    <form className="login-form" onSubmit={handleSubmit}>
      <h1 className="form-title">Glad to have you back!</h1>

      {generalError && (
        <div className="error-banner">
          {generalError}
          <button 
            type="button" 
            className="error-close"
            onClick={() => setGeneralError('')}
          >
            ×
          </button>
        </div>
      )}

      {otpSent && isOTPMode && (
        <div className="success-banner">
          OTP sent successfully! Please check your phone.
        </div>
      )}

      {!isOTPMode ? (
        <>
          <InputField
            label="Email Address"
            type="email"
            name="email"
            placeholder="Eg: Johndoe@email.com"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />

          <PasswordInput
            label="Enter Password"
            name="password"
            placeholder="Enter password here"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
          />

          <div className="login-options">
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              <span>Remember me</span>
            </label>
            <button type="button" className="forgot-link" onClick={onSwitchToForgotPassword}>
              Forgot password?
            </button>
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? 'LOGGING IN...' : 'LOG IN'}
          </button>

          <button 
            type="button" 
            className="otp-btn"
            onClick={handleOTPLogin}
          >
            LOG IN USING OTP
          </button>
        </>
      ) : (
        <>
          <PhoneInput
            label="Phone Number"
            name="phone"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={handleChange}
            error={errors.phone}
            disabled={otpSent}
          />

          {otpSent && (
            <InputField
              label="Enter OTP"
              type="text"
              name="otp"
              placeholder="Enter 6-digit OTP"
              value={formData.otp}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value) && value.length <= 6) {
                  handleChange(e);
                }
              }}
              error={errors.otp}
              maxLength={6}
              inputMode="numeric"
            />
          )}

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {otpSent ? 'VERIFY OTP' : 'SEND OTP'}
          </button>

          <button 
            type="button" 
            className="otp-btn"
            onClick={handlePasswordLogin}
          >
            BACK TO PASSWORD LOGIN
          </button>
        </>
      )}

      <p className="signup-link">
        Don't have an account?{' '}
        <button type="button" onClick={onSwitchToSignup} className="link-btn">
          Click here to Sign Up!
        </button>
      </p>
    </form>
  );
}

export default LoginForm;
